import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Seo, { Breadcrumbs } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { Check, ChevronRight, CreditCard, ShieldCheck, FileText, Fuel, Upload, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${BACKEND}${u}`);

/**
 * Small inline upload widget for a single verification document (license or insurance).
 * Shows a placeholder → uploading → thumbnail with a clear/replace button.
 */
function DocumentUpload({ label, testId, value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const pick = () => inputRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/bookings/upload-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(`${label} photo uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try a smaller image");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="mt-3">
      <Label>{label} photo (required)</Label>
      <input type="file" accept="image/*" ref={inputRef} onChange={onFile} className="hidden" data-testid={`${testId}-input`} />
      {value ? (
        <div className="mt-1 flex items-center gap-3">
          <a href={absUrl(value)} target="_blank" rel="noreferrer">
            <img src={absUrl(value)} alt={`${label} preview`} className="h-20 w-32 object-cover rounded-md border border-white/10" data-testid={`${testId}-preview`} />
          </a>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[#AEB9C9]">Looks good.</span>
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-red-400 hover:text-red-300 inline-flex items-center gap-1"
              data-testid={`${testId}-clear`}
            ><X size={12} /> Replace</button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="mt-1 w-full flex items-center justify-center gap-2 border border-dashed border-white/20 hover:border-[#E2AB20] rounded-lg py-4 text-sm text-[#AEB9C9] hover:text-white transition-colors"
          data-testid={`${testId}-btn`}
        >
          <Upload size={14} /> {uploading ? "Uploading…" : `Upload a photo of your ${label.toLowerCase()}`}
        </button>
      )}
    </div>
  );
}

export default function Booking() {
  const [params] = useSearchParams();
  const slug = params.get("slug");
  const start = params.get("start");
  const end = params.get("end");
  const nav = useNavigate();
  const { user } = useAuth();

  const [v, setV] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    driver_name: "",
    driver_email: "",
    driver_phone: "",
    license_number: "",
    license_state: "CA",
    license_expiry: "",
    insurance_provider: "",
    insurance_policy: "",
    insurance_expiry: "",
    accepted_terms: false,
    referral_code: "",
    license_image_url: "",
    insurance_image_url: "",
  });

  useEffect(() => {
    if (!slug) return;
    api.get(`/vehicles/${slug}`).then((r) => setV(r.data));
  }, [slug]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, driver_name: user.name || f.driver_name, driver_email: user.email || f.driver_email }));
  }, [user]);

  const days = useMemo(() => {
    if (!start || !end) return 0;
    const s = new Date(start), e = new Date(end);
    return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
  }, [start, end]);

  const subtotal = v && days ? days * v.price_per_day : 0;
  const dealTiers = (v?.deal_tiers || []).slice().sort((a, b) => (a.min_days || 0) - (b.min_days || 0));
  const activeTier = dealTiers.reduce((best, t) => (days >= (t.min_days || 0) ? t : best), null);
  const tier = { pct: activeTier?.pct || 0, label: activeTier?.label || null };
  const tierSavings = +(subtotal * tier.pct).toFixed(2);
  const discountedSubtotal = subtotal - tierSavings;
  const discountApplies = !!form.referral_code && form.referral_code.trim().length >= 4;
  const referralSavings = discountApplies ? +(discountedSubtotal * 0.10).toFixed(2) : 0;
  const totalSavings = +(tierSavings + referralSavings).toFixed(2);
  const taxes = +((subtotal - totalSavings) * 0.0875).toFixed(2);
  const total = +(subtotal - totalSavings + taxes).toFixed(2);
  const effectivePerDay = days ? +((subtotal - totalSavings) / days).toFixed(2) : (v?.price_per_day || 0);
  const isEV = !!v && /electric/i.test(v.type || "");

  if (!slug || !start || !end) return <div className="container-bl py-20">Missing booking parameters. <a href="/vehicles" className="link-blue">Go back</a></div>;
  if (!v) return <div className="container-bl py-20">Loading…</div>;

  const submit = async () => {
    try {
      const { data } = await api.post("/bookings", {
        vehicle_id: v.vehicle_id,
        start_date: start,
        end_date: end,
        ...form,
      });
      toast.success("Booking confirmed!");
      nav(`/booking/${data.booking_id}/confirmed`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const steps = [
    { n: 1, label: "Trip details" },
    { n: 2, label: "Verification" },
    { n: 3, label: "Payment" },
  ];

  const canNext = () => {
    if (step === 1) return form.driver_name && form.driver_email && form.driver_phone;
    if (step === 2) return form.license_number && form.license_expiry && form.insurance_provider && form.insurance_policy && form.insurance_expiry && form.license_image_url && form.insurance_image_url;
    if (step === 3) return form.accepted_terms;
    return true;
  };

  return (
    <div className="container-bl py-10" data-testid="page-booking">
      <Seo title={`Book ${v.name} · B&L Rentals`} description={`Complete your ${v.name} rental in Oxnard from ${start} to ${end}.`} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Vehicles", to: "/vehicles" }, { label: v.name, to: `/vehicles/${v.slug}` }, { label: "Book" }]} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-white">Complete your booking</h1>

          {/* Stepper */}
          <div className="mt-6 flex items-center gap-3 text-sm">
            {steps.map((s, i) => (
              <div key={s.n} className="flex items-center gap-3">
                <div className={`h-8 w-8 rounded-full grid place-items-center font-semibold ${step >= s.n ? "bg-[#E2AB20] text-white" : "bg-gray-100 text-[#AEB9C9]"}`}>{step > s.n ? <Check size={16} /> : s.n}</div>
                <span className={`${step === s.n ? "text-white font-medium" : "text-[#AEB9C9]"}`}>{s.label}</span>
                {i < steps.length - 1 && <ChevronRight size={16} className="text-gray-300" />}
              </div>
            ))}
          </div>

          <div className="mt-8 card-bl p-6 md:p-8">
            {step === 1 && (
              <div className="space-y-6" data-testid="step-driver">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-white">Driver details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label>Full name</Label>
                      <Input data-testid="driver-name" value={form.driver_name} onChange={(e) => setForm({...form, driver_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" data-testid="driver-email" value={form.driver_email} onChange={(e) => setForm({...form, driver_email: e.target.value})} />
                    </div>
                    <div>
                      <Label>Phone (US)</Label>
                      <Input data-testid="driver-phone" placeholder="(805) 290-8431" value={form.driver_phone} onChange={(e) => setForm({...form, driver_phone: e.target.value})} />
                    </div>
                    <div>
                      <Label>Referral code (optional)</Label>
                      <Input data-testid="referral-code" placeholder="BL123ABC" value={form.referral_code} onChange={(e) => setForm({...form, referral_code: e.target.value.toUpperCase()})} />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h2 className="text-xl font-heading font-semibold text-white flex items-center gap-2">
                    <Fuel size={18} className="text-[#E2AB20]" /> Miles, fuel & charge policy
                  </h2>
                  <p className="text-sm text-[#AEB9C9] mt-1">One straightforward policy — no surprise upsells.</p>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rental-policy">
                    <div className="rounded-xl border-2 border-[#E2AB20] bg-blue-50/40 p-4">
                      <div className="flex items-center justify-between">
                        <div className="font-heading font-semibold text-white">Unlimited miles</div>
                        <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-[#E2AB20]/10 text-[#E2AB20]">Included</span>
                      </div>
                      <p className="mt-2 text-xs text-[#AEB9C9] leading-relaxed">
                        Drive as far as you'd like within California. No mileage caps, no per-mile charges — ever.
                      </p>
                    </div>
                    <div className="rounded-xl border-2 border-gray-200 bg-white p-4" data-testid="fuel-policy-card">
                      <div className="flex items-center justify-between">
                        <div className="font-heading font-semibold text-white">
                          {isEV ? "Limited charge only" : "Limited gas only"}
                        </div>
                        <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-[#E5EAF2]">Return same level</span>
                      </div>
                      <p className="mt-2 text-xs text-[#AEB9C9] leading-relaxed">
                        {isEV
                          ? "Return the vehicle with the same battery charge level you picked it up with. Otherwise you'll be charged the charging amount plus a $20 service fee."
                          : "Return the vehicle with the same fuel level you picked it up with. Otherwise you'll be charged the fuel amount plus a $20 service fee."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6" data-testid="step-verification">
                <div>
                  <h2 className="text-xl font-heading font-semibold text-white flex items-center gap-2"><FileText size={18} /> Driver license</h2>
                  <p className="text-sm text-[#AEB9C9]">Type the details and upload a clear photo — the host reviews before pickup. Bring your physical license too.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                    <div>
                      <Label>License number</Label>
                      <Input data-testid="license-number" value={form.license_number} onChange={(e) => setForm({...form, license_number: e.target.value})} />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input data-testid="license-state" value={form.license_state} onChange={(e) => setForm({...form, license_state: e.target.value.toUpperCase()})} />
                    </div>
                    <div>
                      <Label>Expiry (YYYY-MM-DD)</Label>
                      <Input data-testid="license-expiry" placeholder="2028-06-30" value={form.license_expiry} onChange={(e) => setForm({...form, license_expiry: e.target.value})} />
                    </div>
                  </div>
                  <DocumentUpload
                    label="License"
                    testId="license-image"
                    value={form.license_image_url}
                    onChange={(url) => setForm({ ...form, license_image_url: url })}
                  />
                </div>
                <div className="border-t border-white/10 pt-6">
                  <h2 className="text-xl font-heading font-semibold text-white flex items-center gap-2"><ShieldCheck size={18} /> Auto insurance</h2>
                  <p className="text-sm text-[#AEB9C9]">You must have an active personal auto policy that transfers to rentals.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                    <div>
                      <Label>Provider</Label>
                      <Input data-testid="insurance-provider" placeholder="State Farm, GEICO…" value={form.insurance_provider} onChange={(e) => setForm({...form, insurance_provider: e.target.value})} />
                    </div>
                    <div>
                      <Label>Policy #</Label>
                      <Input data-testid="insurance-policy" value={form.insurance_policy} onChange={(e) => setForm({...form, insurance_policy: e.target.value})} />
                    </div>
                    <div>
                      <Label>Expiry (YYYY-MM-DD)</Label>
                      <Input data-testid="insurance-expiry" value={form.insurance_expiry} onChange={(e) => setForm({...form, insurance_expiry: e.target.value})} />
                    </div>
                  </div>
                  <DocumentUpload
                    label="Insurance card"
                    testId="insurance-image"
                    value={form.insurance_image_url}
                    onChange={(url) => setForm({ ...form, insurance_image_url: url })}
                  />
                </div>
                <div className="rounded-lg border border-[#E2AB20]/40 bg-[#E2AB20]/5 text-[#E5EAF2] text-sm p-4">
                  Your documents are reviewed by the host before your rental starts. You'll get an email once they're approved.
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5" data-testid="step-payment">
                <h2 className="text-xl font-heading font-semibold text-white flex items-center gap-2"><CreditCard size={18} /> Payment</h2>
                <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm p-4">
                  Payment is mocked in this MVP. Confirming your booking marks it as paid — no card charged.
                </div>
                <div className="flex items-start gap-3">
                  <Checkbox id="terms" checked={form.accepted_terms} onCheckedChange={(v) => setForm({...form, accepted_terms: !!v})} data-testid="accept-terms" />
                  <label htmlFor="terms" className="text-sm text-[#E5EAF2] leading-relaxed">
                    I accept the <a href="/terms" target="_blank" className="link-blue">terms & cancellation policy</a> and confirm my license and insurance are active and valid.
                  </label>
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="prev-step-btn">Back</Button>
              {step < 3 ? (
                <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="bg-[#E2AB20] hover:bg-[#D89B08]" data-testid="next-step-btn">Continue</Button>
              ) : (
                <Button onClick={submit} disabled={!canNext()} className="bg-[#E2AB20] hover:bg-[#D89B08]" data-testid="confirm-booking-btn">Confirm booking</Button>
              )}
            </div>
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-1">
          <div className="card-bl overflow-hidden sticky top-24" data-testid="booking-summary">
            <div className="aspect-[4/3] bg-gray-100">
              <img src={v.hero_image} alt={v.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">{v.type} · {v.year}</div>
              <h3 className="mt-1 font-heading font-semibold text-lg">{v.name}</h3>
              <div className="mt-3 text-sm text-[#AEB9C9]">
                <div>Pickup · <b>{start}</b></div>
                <div>Return · <b>{end}</b></div>
                <div>{days} day{days > 1 ? "s" : ""}</div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm border-t pt-4">
                <div className="flex justify-between"><span>${v.price_per_day} × {days}</span><span>${subtotal.toFixed(2)}</span></div>
                {tier.label && (
                  <div className="flex justify-between text-green-700 font-medium" data-testid="summary-tier">
                    <span>{tier.label} ({Math.round(tier.pct * 100)}% off)</span>
                    <span>-${tierSavings.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#AEB9C9] text-xs" data-testid="summary-policy">
                  <span>Unlimited miles</span><span>Included</span>
                </div>
                <div className="flex justify-between text-[#AEB9C9] text-xs">
                  <span>{isEV ? "Limited charge" : "Limited gas"}</span><span>Return same level</span>
                </div>
                {referralSavings > 0 && <div className="flex justify-between text-green-700"><span>Referral discount (10%)</span><span>-${referralSavings.toFixed(2)}</span></div>}
                <div className="flex justify-between text-[#AEB9C9]"><span>Taxes (8.75%)</span><span>${taxes.toFixed(2)}</span></div>
                <div className="flex justify-between font-bold text-white border-t pt-2"><span>Total</span><span data-testid="summary-total">${total.toFixed(2)}</span></div>
                {totalSavings > 0 && (
                  <div className="mt-3 rounded-lg bg-green-50 border border-green-200 p-3 text-xs text-green-800" data-testid="summary-savings">
                    <div className="font-semibold">You're saving ${totalSavings.toFixed(2)}!</div>
                    <div className="mt-0.5">Effective rate: <b>${effectivePerDay.toFixed(2)}/day</b> (normally ${v.price_per_day}/day)</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
